import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  Typography,
  Tabs,
  Tab,
  Alert,
  Snackbar,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import moment from "moment";

import FetchHook from "../../Api/FetchHooks";
import { formatDate, getKycChip, getUserDetails } from "../../utils/helpers";
import TableComponent from "../../Components/TableComponent";
import PageLoader from "../../Components/PageLoader";
import ErrorDisplay from "../../Components/ErrorDisplay";
import Page from "../../Components/Page";
import { useSelector } from "react-redux";
import { AllInbox } from "@mui/icons-material";
import UseServerTable from "../../Hooks/UseServerTable";

const Products = () => {
  const navigate = useNavigate();
  const loggedInUser = useSelector((state) => state.auth.user);

  const [activeTab, setActiveTab] = useState("active");
  const [data, setData] = useState([]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  // const [loading, response, request] = FetchHook();

  // // Data fetching is now tied directly to the activeTab state
  // useEffect(() => {
  //   request({ url: `product?status="${activeTab}"` });
  // }, [activeTab, request]);

  const { gridProps, error, response, refresh } = UseServerTable({
    baseUrl: `/product?status=${activeTab}`,
    onSuccess: (data) => {
      setData(data.Loan);
    },
  });

  useEffect(() => {
    if (error) {
      setSnackbar({
        open: true,
        message: error.message,
        severity: error.severity,
      });
    }
  }, [error]);

  const columns = [
    { field: "id", headerName: "Product ID", flex: 1, minWidth: 150 },
    { field: "loan_name", headerName: "Loan Name", flex: 1, minWidth: 180 },
    {
      field: "loan_type",
      headerName: "Loan Type",
      flex: 1,
      minWidth: 140,
    },
    {
      field: "active_status",
      headerName: "Status",
      flex: 0.5,
      minWidth: 100,
      renderCell: (params) => getKycChip(params.value),
    },
    {
      field: "publication_date",
      headerName: "Date Created",
      flex: 1,
      minWidth: 150,
      valueFormatter: (params) => formatDate(params?.value),
    },
    {
      field: "actions",
      headerName: "Actions",
      // align: "right",
      // headerAlign: "right",
      sortable: false,
      renderCell: ({ row }) => (
        <Button
          variant="outlined"
          size="small"
          onClick={() =>
            navigate("/dashboard/products/product-profile", { state: row })
          }
        >
          View Profile
        </Button>
      ),
      flex: 0.5,
      minWidth: 150,
    },
  ];

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  // Determine the content based on loading, response, and error states
  const renderContent = () => {
    const isError = error;
    const products = data || [];

    if (gridProps.loading) {
      return <PageLoader />;
    }

    if (isError) {
      setSnackbar({
        open: true,
        message: error.message,
        severity: error.severity,
      });
      return (
        <ErrorDisplay
          message={error.message}
          icon={
            <AllInbox color="secondary" sx={{ mx: "auto", fontSize: 80 }} />
          }
        />
      );
    }

    return (
      <TableComponent
        columns={columns}
        data={products}
        sortModel={[
          {
            field: "publication_date",
            sort: "desc",
          },
        ]}
        {...gridProps}
      />
    );
  };

  return (
    <Page
      title={
        <Typography variant="h4" fontWeight="bold">
          Product Management
        </Typography>
      }
      actions={
        <Box>
          {loggedInUser?.role === "admin" && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate("/dashboard/products/create-product")}
            >
              Add New Product
            </Button>
          )}
        </Box>
      }
    >
      <Card
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          height: "100%",
        }}
      >
        <Box sx={{ borderBottom: 1, borderColor: "divider", flexShrink: 0 }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            aria-label="product status tabs"
          >
            <Tab label="Active" value="active" />
            <Tab label="Inactive" value="inactive" />
            <Tab label="All" value="all" />
          </Tabs>
        </Box>

        <CardContent
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            p: 2,
            overflow: "hidden",
            "& .MuiDataGrid-root": {
              // border: "none",
            },
          }}
        >
          {renderContent()}
        </CardContent>
      </Card>
    </Page>
  );
};

export default Products;
